import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  format, parseISO, addMonths, subMonths,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameDay, isSameMonth, isToday,
} from 'date-fns'
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { getDateLocale } from '@/lib/utils/date'

interface DatePickerProps {
  value: string        // YYYY-MM-DD or ''
  onChange: (val: string) => void
  label?: string
  error?: string
  placeholder?: string
  isDateDisabled?: (date: Date) => boolean
}

export function DatePicker({ value, onChange, label, error, placeholder = 'Select date', isDateDisabled }: DatePickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)

  const selected = value ? parseISO(value) : null
  const [viewDate, setViewDate] = useState<Date>(() => selected ?? new Date())

  useEffect(() => {
    if (value) setViewDate(parseISO(value))
  }, [value])

  const updatePos = useCallback(() => {
    if (!buttonRef.current) return
    const r = buttonRef.current.getBoundingClientRect()
    const viewportPad = 8
    const viewportWidth = window.visualViewport?.width ?? window.innerWidth
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight
    const calendarWidth = Math.min(288, viewportWidth - viewportPad * 2)
    const calendarHeight = Math.min(340, viewportHeight - viewportPad * 2)
    const left = Math.min(
      Math.max(r.left, viewportPad),
      Math.max(viewportPad, viewportWidth - calendarWidth - viewportPad),
    )
    const below = r.bottom + 6
    const top = below + calendarHeight <= viewportHeight - viewportPad
      ? below
      : Math.max(viewportPad, r.top - calendarHeight - 6)
    setPos({ top, left, width: calendarWidth })
  }, [])

  const handleOpen = () => {
    updatePos()
    setOpen((o) => !o)
  }

  // Close on outside click or scroll
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onScroll = () => { updatePos() }
    document.addEventListener('mousedown', close)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    window.visualViewport?.addEventListener('resize', onScroll)
    return () => {
      document.removeEventListener('mousedown', close)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      window.visualViewport?.removeEventListener('resize', onScroll)
    }
  }, [open, updatePos])

  const calDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(viewDate), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(viewDate), { weekStartsOn: 1 }),
  })

  const handleSelect = (day: Date) => {
    if (isDateDisabled?.(day)) return
    onChange(format(day, 'yyyy-MM-dd'))
    setOpen(false)
  }

  const calendar = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.97 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999, width: pos.width, maxHeight: 'calc(100dvh - 1rem)' }}
          className="max-h-[min(22rem,calc(100dvh-1rem))] overflow-y-auto overscroll-contain rounded-[var(--radius-lg)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-3 shadow-2xl select-none"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setViewDate((d) => subMonths(d, 1))}
              aria-label="Previous month"
              className="p-1.5 rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold text-[var(--color-text-primary)]">
              {format(viewDate, 'MMMM yyyy', { locale: getDateLocale() })}
            </span>
            <button
              type="button"
              onClick={() => setViewDate((d) => addMonths(d, 1))}
              aria-label="Next month"
              className="p-1.5 rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Weekday headers — derived from locale so they translate automatically */}
          <div className="grid grid-cols-7 mb-1">
            {[1,2,3,4,5,6,0].map((dow) => {
              // Get a reference date for that weekday (2021-01-04 = Monday)
              const ref = new Date(2021, 0, 3 + dow) // Jan 3 = Sunday, Jan 4 = Monday, …
              return (
                <div key={dow} className="text-center text-[10px] font-semibold text-[var(--color-text-muted)] py-1 uppercase tracking-wide">
                  {format(ref, 'EEEEE', { locale: getDateLocale() })}
                </div>
              )
            })}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {calDays.map((day) => {
              const isSelected = selected ? isSameDay(day, selected) : false
              const inMonth = isSameMonth(day, viewDate)
              const today = isToday(day)
              const disabled = isDateDisabled?.(day) ?? false
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => handleSelect(day)}
                  disabled={disabled}
                  title={disabled ? 'Closed' : undefined}
                  className={`mx-auto flex h-9 w-9 items-center justify-center rounded-full text-xs transition-colors ${
                    disabled
                      ? 'text-[var(--color-text-muted)]/30 cursor-not-allowed line-through'
                      : isSelected
                      ? 'bg-[var(--color-accent-amber)] text-black font-bold'
                      : today
                      ? 'border border-[var(--color-accent-amber)]/60 text-[var(--color-accent-amber)] hover:bg-[var(--color-bg-elevated)]'
                      : inMonth
                      ? 'text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]'
                      : 'text-[var(--color-text-muted)]/40 hover:bg-[var(--color-bg-elevated)]'
                  }`}
                >
                  {format(day, 'd')}
                </button>
              )
            })}
          </div>

          {/* Today shortcut */}
          {!(isDateDisabled?.(new Date())) && (
            <div className="mt-2 pt-2 border-t border-[var(--color-border-subtle)]">
              <button
                type="button"
                onClick={() => handleSelect(new Date())}
                className="w-full text-center text-xs text-[var(--color-text-muted)] hover:text-[var(--color-accent-amber)] transition-colors py-0.5"
              >
                {t('bookings.today')}
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )

  return (
    <div className="space-y-1.5">
      {label && (
        <label className="block text-sm font-medium text-[var(--color-text-secondary)]">{label}</label>
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        className={`flex h-11 w-full items-center gap-2 rounded-[var(--radius-md)] border bg-[var(--color-bg-elevated)] px-3 text-left text-sm transition-colors sm:h-10 ${
          error
            ? 'border-[var(--color-accent-red)]'
            : open
            ? 'border-[var(--color-accent-amber)]/60 ring-1 ring-[var(--color-accent-amber)]/30'
            : 'border-[var(--color-border-default)] hover:border-[var(--color-border-subtle)]'
        }`}
      >
        <CalendarDays className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
        <span className={selected ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-muted)]'}>
          {selected ? format(selected, 'EEE, MMM d yyyy', { locale: getDateLocale() }) : placeholder}
        </span>
      </button>
      {error && <p className="text-xs text-[var(--color-accent-red)]">{error}</p>}

      {createPortal(calendar, document.body)}
    </div>
  )
}
